import moment from "moment/moment";
import React from "react";
import { useState } from "react";
import { useEffect } from "react";
import { Spinner, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { getStudentFees } from "../../../store/actions/studentAction/feesAction";
import {
  courseType,
  examType,
  getSemester,
  getYears,
} from "../../../utils/constant";

/*
  Fees Status, in the student's own login.

  This page used to be three separate tables, one per course type, and only
  the course-wise one worked. The semester table read the date from a column
  that does not exist, and the yearly one asked the list of fees for the
  course duration - which belongs to the course - so it never worked out how
  many years to show. A student with nothing paid yet was shown the sentence
  "Fees Not Paid" and no table at all, so there was nothing to see for a
  semester or yearly course.

  One table now, built the same way for every course: a line for each payment
  received, and a line for each term still to pay.
*/
const StudentFees = () => {
  const [exist, setExist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const dispatch = useDispatch();
  const { studentAuth } = useSelector((state) => state.student);
  const { profile } = studentAuth;
  const course = profile?.course || {};

  useEffect(() => {
    dispatch(getStudentFees(setLoading, setExist, setData));
  }, [dispatch]);

  // The endpoint answers with the string "null" when nothing has been paid,
  // so anything that is not a real list counts as no payments.
  const fees = exist && Array.isArray(data) ? data : [];

  // Course-wise and subject-wise courses are paid once, at registration.
  // Semester and yearly courses are paid again each term.
  const courseWise =
    course.exam_type === examType.course_wise ||
    course.exam_type === examType.subject_wise;
  const isSemester = String(course.course_type) === courseType.semester;
  const termWord = isSemester ? "Semester" : "Year";
  const termCount = courseWise
    ? 0
    : isSemester
    ? getSemester(course.duration)
    : getYears(course.duration);

  const paidTerms = fees.map((fee) => Number(fee.semester));
  const pendingTerms = Array.from({ length: termCount }, (_, i) => i + 1).filter(
    (term) => !paidTerms.includes(term)
  );

  // paid_at is when the money was received; older rows only carry created_at.
  const feeDate = (fee) => {
    const when = fee.paid_at || fee.created_at;
    return when ? moment(when).format("DD/MM/YYYY") : "--";
  };

  return (
    <div className="Title-box">
      <div className="row">
        <div className="col-md-6">
          <div className="BigTitle">Fees Status</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" size="lg">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Fees</th>
              {!courseWise && <th>{termWord}</th>}
              <th>Date</th>
              <th>Status</th>
              <th>Receipt</th>
            </tr>
          </thead>
          <tbody>
            {fees.map((fee) => (
              <tr key={`paid-${fee.id}`}>
                <td>{fee.total_fees}</td>
                {!courseWise && <td>{fee.semester}</td>}
                <td>{feeDate(fee)}</td>
                <td>Paid</td>
                <td>
                  {fee.link ? (
                    <a target="_blank" rel="noreferrer" href={fee.link}>
                      View
                    </a>
                  ) : (
                    "NA"
                  )}
                </td>
              </tr>
            ))}

            {pendingTerms.map((term) => (
              <tr key={`pending-${term}`}>
                <td>--</td>
                <td>{term}</td>
                <td>--</td>
                <td>Pending</td>
                <td>NA</td>
              </tr>
            ))}

            {/* A course paid once, with nothing received yet. */}
            {courseWise && fees.length === 0 && (
              <tr>
                <td>--</td>
                <td>--</td>
                <td>Pending</td>
                <td>NA</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default StudentFees;
